import { Link } from "@tanstack/react-router";
import { Fragment, type ReactNode, useCallback } from "react";
import type { SearchMeta, ServiceCategory, VenueCard as VenueCardType } from "@/api/types";
import { SearchFailure } from "../search-failure";
import { ResultsSkeleton } from "./results-skeleton";
import { VenueCard } from "./venue-card";

function hasSearchQuery(query?: string): boolean {
  return Boolean(query && query !== "*");
}

function CategoryEmphasis({ children }: { children: ReactNode }) {
  return <strong className="text-ink">{children}</strong>;
}

interface ResultsCountLabelProps {
  total: number;
  activeCategoryLabel?: string;
  query?: string;
}

function ResultsCountLabel({ total, activeCategoryLabel, query }: ResultsCountLabelProps) {
  const hasCategories = Boolean(activeCategoryLabel);
  const hasQuery = hasSearchQuery(query);

  return (
    <>
      {total} {total === 1 ? "result" : "results"}
      {hasCategories && hasQuery && (
        <>
          {" "}
          for <CategoryEmphasis>{activeCategoryLabel}</CategoryEmphasis> matching &ldquo;{query}
          &rdquo;
        </>
      )}
      {hasCategories && !hasQuery && (
        <>
          {" "}
          for <CategoryEmphasis>{activeCategoryLabel}</CategoryEmphasis>
        </>
      )}
      {!hasCategories && hasQuery && <> for &ldquo;{query}&rdquo;</>}
    </>
  );
}

function outsiderSeparatorIndex(results: VenueCardType[]): number | null {
  const firstOutsiderIndex = results.findIndex((result) => result.inside_selected_area === false);
  if (firstOutsiderIndex <= 0) return null;

  const hasInArea = results
    .slice(0, firstOutsiderIndex)
    .some((result) => result.inside_selected_area === true);
  if (!hasInArea) return null;

  return firstOutsiderIndex;
}

interface ResultsPanelProps {
  results?: VenueCardType[];
  meta?: SearchMeta;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  onRetry?: () => void;
  isRetrying?: boolean;
  hoveredVenueId: string | null;
  onHover: (id: string | null) => void;
  activeCategoryNames?: string[];
  query?: string;
  onPageChange: (page: number) => void;
  categories?: ServiceCategory[];
}

// fallow-ignore-next-line complexity
function ResultsPanel({
  results,
  meta,
  isLoading,
  isError,
  error,
  onRetry,
  isRetrying,
  hoveredVenueId,
  onHover,
  activeCategoryNames,
  query,
  onPageChange,
  categories,
}: ResultsPanelProps) {
  const handleCardHover = useCallback(
    (id: string | null) => {
      onHover(id);
    },
    [onHover],
  );

  if (isLoading) {
    return (
      <div className="pt-4">
        <ResultsSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-10">
        <SearchFailure error={error} onRetry={onRetry} isRetrying={isRetrying} />
      </div>
    );
  }

  if (!results || results.length === 0) {
    return (
      <SearchEmpty query={query} activeCategories={activeCategoryNames} categories={categories} />
    );
  }

  const total = meta?.total ?? results.length;
  const perPage = meta?.per_page ?? 20;
  const currentPage = meta?.page ?? 1;
  const totalPages = Math.ceil(total / perPage);
  const activeCategoryLabel = activeCategoryNames?.join(", ");
  const separatorIndex = outsiderSeparatorIndex(results);

  return (
    <>
      <div className="pt-4 text-[13px] text-slate">
        <ResultsCountLabel total={total} activeCategoryLabel={activeCategoryLabel} query={query} />
      </div>

      <div className="grid grid-cols-1 gap-4 pt-4 lg:grid-cols-2">
        {results.map((venue, index) => (
          <Fragment key={venue.id}>
            {separatorIndex === index && (
              <div className="lg:col-span-2 flex items-center gap-3 py-1 text-[12px] text-slate">
                <span className="h-px flex-1 bg-fog" />
                <span className="font-medium">Maybe something a little further?</span>
                <span className="h-px flex-1 bg-fog" />
              </div>
            )}
            <div>
              <VenueCard
                venue={venue}
                onHover={handleCardHover}
                isHighlighted={hoveredVenueId === venue.id}
              />
            </div>
          </Fragment>
        ))}
      </div>

      {totalPages > 1 && (
        <SearchPagination
          currentPage={currentPage}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
}

function SearchPagination({
  currentPage,
  totalPages,
  total,
  perPage,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  total: number;
  perPage: number;
  onPageChange: (page: number) => void;
}) {
  const start = (currentPage - 1) * perPage + 1;
  const end = Math.min(currentPage * perPage, total);

  return (
    <>
      <div className="pt-6 text-center text-[13px] text-slate">
        Showing {start}&ndash;{end} of {total} results
      </div>
      <nav
        className="flex flex-wrap justify-center gap-1 pb-12 pt-4"
        aria-label="Search results pages"
      >
        {currentPage > 1 && (
          <PageButton onClick={() => onPageChange(currentPage - 1)} label="Previous page">
            &laquo;
          </PageButton>
        )}
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
          <PageButton
            key={pg}
            active={pg === currentPage}
            onClick={() => onPageChange(pg)}
            ariaCurrent={pg === currentPage ? "page" : undefined}
          >
            {pg}
          </PageButton>
        ))}
        {currentPage < totalPages && (
          <PageButton onClick={() => onPageChange(currentPage + 1)} label="Next page">
            &raquo;
          </PageButton>
        )}
      </nav>
    </>
  );
}

function PageButton({
  active,
  onClick,
  label,
  ariaCurrent,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  label?: string;
  ariaCurrent?: "page";
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`inline-flex h-10 min-w-[40px] cursor-pointer items-center justify-center rounded-pill border px-3 text-[14px] font-medium transition-all duration-short ease-default ${
        active
          ? "border-electric-700 bg-electric-50 text-electric-800"
          : "border-fog bg-surface text-ink hover:bg-fog"
      }`}
      onClick={onClick}
      aria-label={label}
      aria-current={ariaCurrent}
    >
      {children}
    </button>
  );
}

// fallow-ignore-next-line complexity
function SearchEmpty({
  query,
  activeCategories,
  categories,
}: {
  query?: string;
  activeCategories?: string[];
  categories?: ServiceCategory[];
}) {
  const activeCategoryLabel = activeCategories?.join(", ");
  const hasActiveCategories = (activeCategories?.length ?? 0) > 0;

  let description: React.ReactNode;
  if (hasSearchQuery(query) && hasActiveCategories) {
    description = (
      <>
        No venues for <strong>{activeCategoryLabel}</strong> matching &ldquo;{query}&rdquo;. Try a
        different search or browse by category.
      </>
    );
  } else if (hasSearchQuery(query)) {
    description = (
      <>No venues match &ldquo;{query}&rdquo;. Try a different search or browse by category.</>
    );
  } else if (hasActiveCategories) {
    description = (
      <>
        No venues found for <strong>{activeCategoryLabel}</strong>. Try another category or clear
        your filters.
      </>
    );
  } else {
    description = (
      <>We couldn&rsquo;t find any venues matching your criteria. Try browsing by category below.</>
    );
  }

  return (
    <div className="px-5 py-[60px] text-center">
      <div className="mb-5 text-slate">
        <svg
          width="64"
          height="64"
          viewBox="0 0 64 64"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="mx-auto"
        >
          <circle cx="28" cy="28" r="18" />
          <path d="M41 41 56 56" />
          <path d="M25 22v2M31 22v2M28 30c-3 0-5 2-5 2" strokeWidth={2} />
        </svg>
      </div>

      <h2 className="font-display font-semibold text-[22px] mb-2">No results found</h2>

      <p className="mx-auto max-w-[420px] mb-7 text-[15px] leading-normal text-slate">
        {description}
      </p>

      {categories && categories.length > 0 && (
        <div className="mb-6">
          <p className="mb-3 text-[13px] font-medium uppercase tracking-[0.04em] text-slate">
            Browse by category
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {categories.slice(0, 6).map((cat) => (
              <Link
                key={cat.key}
                to="/search"
                search={{ category: cat.key }}
                className="inline-flex h-8 items-center rounded-pill bg-fog px-3.5 text-[13px] font-medium text-ink no-underline transition-colors duration-short ease-default hover:bg-fog-dark"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      <Link
        to="/search"
        className="inline-flex h-10 items-center rounded-pill bg-electric-700 px-5 text-[14px] font-semibold text-white no-underline transition-colors duration-short ease-default hover:bg-electric-800"
      >
        Clear search
      </Link>
    </div>
  );
}

export { ResultsPanel, type ResultsPanelProps };
